import type { AvailabilitySchedule, Booking, EventType, Weekday } from "./types";

export const weekdayKeys: Weekday[] = ["sun", "mon", "tue", "wed", "thu", "fri", "sat"];
export const weekdayNames: Record<Weekday, string> = {
  sun: "Sunday",
  mon: "Monday",
  tue: "Tuesday",
  wed: "Wednesday",
  thu: "Thursday",
  fri: "Friday",
  sat: "Saturday"
};


export function cleanText(value: unknown): string {
  return String(value ?? "")
    .replace(/\r\n?/g, "\n")
    .split("\n")
    .map((line) => line.replace(/[ \t]+$/g, ""))
    .join("\n")
    .trim();
}

export function sanitizeJsonValue<T>(value: T): T {
  if (typeof value === "string") return cleanText(value) as T;
  if (Array.isArray(value)) return value.map((item) => sanitizeJsonValue(item)) as T;
  if (value && typeof value === "object") {
    const output: Record<string, unknown> = {};
    Object.entries(value as Record<string, unknown>).forEach(([key, item]) => {
      output[key] = sanitizeJsonValue(item);
    });
    return output as T;
  }
  return value;
}

export function uid(prefix = "id"): string {
  return `${prefix}_${crypto.randomUUID().replaceAll("-", "").slice(0, 16)}`;
}

export function slugify(value: string): string {
  return value
    .toLowerCase()
    .trim()
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/^-|-$/g, "") || "event";
}

export function escapeHtml(value: string): string {
  return value.replace(/[&<>'"]/g, (char) => ({
    "&": "&amp;",
    "<": "&lt;",
    ">": "&gt;",
    "'": "&#039;",
    '"': "&quot;"
  })[char] || char);
}

type TimestampLike = {
  toDate?: () => Date;
  toMillis?: () => number;
  seconds?: number;
  nanoseconds?: number;
  _seconds?: number;
  _nanoseconds?: number;
};

export function dateFromValue(value: unknown): Date | null {
  if (value instanceof Date) {
    return Number.isFinite(value.getTime()) ? value : null;
  }

  if (typeof value === "string" || typeof value === "number") {
    const parsed = new Date(value);
    return Number.isFinite(parsed.getTime()) ? parsed : null;
  }

  if (!value || typeof value !== "object") return null;

  const timestamp = value as TimestampLike;
  try {
    if (typeof timestamp.toDate === "function") {
      const parsed = timestamp.toDate();
      return Number.isFinite(parsed.getTime()) ? parsed : null;
    }
    if (typeof timestamp.toMillis === "function") {
      const parsed = new Date(timestamp.toMillis());
      return Number.isFinite(parsed.getTime()) ? parsed : null;
    }
  } catch {
    return null;
  }

  const seconds = typeof timestamp.seconds === "number"
    ? timestamp.seconds
    : typeof timestamp._seconds === "number"
      ? timestamp._seconds
      : null;
  if (seconds === null) return null;

  const nanoseconds = typeof timestamp.nanoseconds === "number"
    ? timestamp.nanoseconds
    : typeof timestamp._nanoseconds === "number"
      ? timestamp._nanoseconds
      : 0;
  const parsed = new Date(seconds * 1000 + Math.floor(nanoseconds / 1_000_000));
  return Number.isFinite(parsed.getTime()) ? parsed : null;
}

export function dateValueMs(value: unknown): number {
  return dateFromValue(value)?.getTime() ?? 0;
}

export function toIsoDateString(value: unknown): string {
  return dateFromValue(value)?.toISOString() ?? "";
}

export function formatDateTime(value: unknown): string {
  const date = dateFromValue(value);
  if (!date) return "—";
  return new Intl.DateTimeFormat(undefined, {
    dateStyle: "medium",
    timeStyle: "short"
  }).format(date);
}

export function formatTime(value: unknown): string {
  const date = dateFromValue(value);
  if (!date) return "—";
  return new Intl.DateTimeFormat(undefined, { hour: "numeric", minute: "2-digit" }).format(date);
}

export function formatDate(value: unknown): string {
  const date = dateFromValue(value);
  if (!date) return "—";
  return new Intl.DateTimeFormat(undefined, { weekday: "short", month: "short", day: "numeric", year: "numeric" }).format(date);
}

export function isSameLocalDay(a: Date, b: Date): boolean {
  return a.getFullYear() === b.getFullYear() && a.getMonth() === b.getMonth() && a.getDate() === b.getDate();
}

function timeToMinutes(value: string): number {
  const [hour, minute] = value.split(":").map(Number);
  return hour * 60 + minute;
}

function partsInZone(date: Date, timeZone: string): Record<string, string> {
  return Object.fromEntries(new Intl.DateTimeFormat("en-US", {
    timeZone,
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
    hour: "2-digit",
    minute: "2-digit",
    second: "2-digit",
    hourCycle: "h23"
  }).formatToParts(date).map((part) => [part.type, part.value]));
}

function zonedLocalToUtc(dateKey: string, minutes: number, timeZone: string): Date {
  const [year, month, day] = dateKey.split("-").map(Number);
  const hour = Math.floor(minutes / 60);
  const minute = minutes % 60;
  let timestamp = Date.UTC(year, month - 1, day, hour, minute, 0, 0);

  // Resolve the IANA-zone offset iteratively. This also handles daylight-saving boundaries.
  for (let attempt = 0; attempt < 3; attempt += 1) {
    const parts = partsInZone(new Date(timestamp), timeZone);
    const representedAsUtc = Date.UTC(
      Number(parts.year),
      Number(parts.month) - 1,
      Number(parts.day),
      Number(parts.hour),
      Number(parts.minute),
      Number(parts.second)
    );
    timestamp -= representedAsUtc - Date.UTC(year, month - 1, day, hour, minute, 0, 0);
  }

  return new Date(timestamp);
}

function shiftDateKey(dateKey: string, days: number): string {
  const [year, month, day] = dateKey.split("-").map(Number);
  const shifted = new Date(Date.UTC(year, month - 1, day + days));
  return `${shifted.getUTCFullYear()}-${String(shifted.getUTCMonth() + 1).padStart(2, "0")}-${String(shifted.getUTCDate()).padStart(2, "0")}`;
}

function weekdayForDateKey(dateKey: string): Weekday {
  const [year, month, day] = dateKey.split("-").map(Number);
  return weekdayKeys[new Date(Date.UTC(year, month - 1, day)).getUTCDay()];
}

export function getAvailableSlots(
  visitorDate: Date,
  eventType: EventType,
  availability: AvailabilitySchedule,
  bookings: Booking[]
): Date[] {
  const visitorDateKey = toDateInputValue(visitorDate);
  const now = new Date();
  const earliest = new Date(now.getTime() + eventType.minimumNoticeHours * 60 * 60 * 1000);
  const latest = new Date(now.getTime() + eventType.bookingWindowDays * 86400000);
  const slots: Date[] = [];

  // A host-local date can fall on the previous or following invitee-local date, so inspect all three.
  for (const offset of [-1, 0, 1]) {
    const hostDateKey = shiftDateKey(visitorDateKey, offset);
    const override = availability.overrides.find((item) => item.date === hostDateKey);
    if (override?.unavailable) continue;

    const weekday = weekdayForDateKey(hostDateKey);
    const daySettings = availability.weekly[weekday];
    const ranges = override ? override.ranges : daySettings.ranges;
    if (!override && !daySettings.enabled) continue;

    ranges.forEach((range) => {
      let cursor = timeToMinutes(range.start);
      const rangeEnd = timeToMinutes(range.end);
      while (cursor + eventType.durationMinutes <= rangeEnd) {
        const start = zonedLocalToUtc(hostDateKey, cursor, availability.timezone);
        const finish = new Date(start.getTime() + eventType.durationMinutes * 60000);
        if (toDateInputValue(start) !== visitorDateKey) {
          cursor += 30;
          continue;
        }

        const collision = bookings.some((booking) => {
          if (booking.status === "canceled" || booking.status === "failed") return false;
          const existingStart = new Date(booking.startAt).getTime() - eventType.bufferBeforeMinutes * 60000;
          const existingEnd = new Date(booking.endAt).getTime() + eventType.bufferAfterMinutes * 60000;
          return start.getTime() < existingEnd && finish.getTime() > existingStart;
        });

        if (start >= earliest && start <= latest && !collision) slots.push(start);
        cursor += 30;
      }
    });
  }

  return slots.sort((a, b) => a.getTime() - b.getTime());
}

export function toDateInputValue(date: Date): string {
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, "0");
  const day = String(date.getDate()).padStart(2, "0");
  return `${year}-${month}-${day}`;
}

export function monthGrid(anchor: Date): Array<Date | null> {
  const first = new Date(anchor.getFullYear(), anchor.getMonth(), 1);
  const last = new Date(anchor.getFullYear(), anchor.getMonth() + 1, 0);
  const output: Array<Date | null> = Array(first.getDay()).fill(null);
  for (let day = 1; day <= last.getDate(); day += 1) {
    output.push(new Date(anchor.getFullYear(), anchor.getMonth(), day));
  }
  while (output.length % 7 !== 0) output.push(null);
  return output;
}
